import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as apiRequest from '@shared/api-endpoints'
import ROUTE from '@shared/modules/assessment/router/names'
import { sessionStorage } from '@shared/helpers/storage'

// this also defines which questionnaires are known
const questionnaireRoutes = {
  cat: ROUTE.ASSESSMENT_CAT_WELCOME,
  self_test: ROUTE.ASSESSMENT_SELF_TEST_WELCOME,
  self_test_reevaluation: ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_PAIN_AVERAGE,
}

export const useAssessmentStore = defineStore(
  'assessment',
  () => {
    // State
    const finishRoute = ref({ name: ROUTE.ASSESSMENT_COMPLETION })
    const questionnaires = ref([])
    const currentQuestionnaire = ref(0)

    // Getters
    const currentQuestionnaireRoute = computed(
      () =>
        questionnaireRoutes[questionnaires.value[currentQuestionnaire.value]],
    )

    // Actions
    async function getQuestionnaireConfig() {
      const getCheckupsResponse = await apiRequest.getCheckups()

      const currentDate = new Date()
      currentDate.setDate(currentDate.getDate() + 7)

      const weekAheadISODate = currentDate.toISOString()

      const checkup = getCheckupsResponse.data.checkups.filter(
        (m) => m.completed_at === null && weekAheadISODate >= m.due,
      )[0]

      if (checkup === undefined) {
        console.info('no checkup found for user', getCheckupsResponse.data)
        currentQuestionnaire.value = null
        return
      }

      let uncompletedQuestionnaires = checkup.questionnaires
        .filter(
          (q) =>
            q.completed_at === null &&
            // only known questionnaires
            Object.keys(questionnaireRoutes).includes(q.key),
        )
        .map((q) => q.key)

      if (uncompletedQuestionnaires.length === 0) {
        console.info(
          'no questionnaires found for user',
          getCheckupsResponse.data,
        )
        currentQuestionnaire.value = null
        return
      }

      const completedQuestionnaires = checkup.questionnaires
        .filter(
          (q) =>
            q.completed_at !== null &&
            // only known questionnaires
            Object.keys(questionnaireRoutes).includes(q.key),
        )
        .map((q) => q.key)

      if (
        completedQuestionnaires.length === 0 &&
        uncompletedQuestionnaires.length === 1 &&
        uncompletedQuestionnaires[0] === 'self_test'
      ) {
        uncompletedQuestionnaires = ['self_test']
      }

      currentQuestionnaire.value = completedQuestionnaires.length
      questionnaires.value = [
        ...completedQuestionnaires,
        ...uncompletedQuestionnaires,
      ].filter((q) => questionnaireRoutes[q] != null)
    }

    function openCurrentQuestionnaire(router) {
      if (currentQuestionnaireRoute.value !== undefined) {
        router.push({
          name: currentQuestionnaireRoute.value,
        })

        if (
          currentQuestionnaireRoute.value === ROUTE.ASSESSMENT_SELF_TEST_WELCOME
        ) {
          sessionStorage.setItem(
            'assessment:previousQuestionnaire',
            'self_test',
          )
        } else {
          sessionStorage.removeItem('assessment:previousQuestionnaire')
        }
      } else {
        apiRequest.sendBrazeEvent({
          name: 'milestone_assessment_done',
        })

        router.push(finishRoute.value)
      }
    }

    async function completeQuestionnaire(router) {
      await getQuestionnaireConfig()
      openCurrentQuestionnaire(router)
    }

    return {
      // State
      finishRoute,
      questionnaires,
      currentQuestionnaire,
      // Getters
      currentQuestionnaireRoute,
      // Actions
      getQuestionnaireConfig,
      openCurrentQuestionnaire,
      completeQuestionnaire,
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)

export default useAssessmentStore
